import type { ContainerElement, Element, FieldElement, FormDefinition, RepeatableGroupField } from './form-definition';
import { Option } from './pieces';

export const isContainer = (element: Element): element is ContainerElement => {
  return element.kind === 'section' || (element.kind === 'field' && holdsFields(element));
};

export const holdsFields = (field: FieldElement): field is RepeatableGroupField => {
  return field.elements !== undefined;
};

export const childrenOf = (element: Element): Element[] => {
  return isContainer(element) ? element.elements : [];
};

export const allElements = (elements: Element[]): Element[] => {
  const all: Element[] = [];
  const visit = (level: Element[]) => {
    for (const element of level) {
      all.push(element);
      visit(childrenOf(element));
    }
  };
  visit(elements);
  return all;
};

export const fieldsOf = (elements: Element[]): FieldElement[] =>
  elements.flatMap(element => (element.kind === 'field' ? [element] : fieldsOf(childrenOf(element))));

export const findElement = (definition: FormDefinition, id: string): Element | undefined => {
  return allElements(definition.elements).find(element => element.id === id);
};

export const findField = (definition: FormDefinition, id: string): FieldElement | undefined => {
  const element = findElement(definition, id);
  return element?.kind === 'field' ? element : undefined;
};

export const parentOf = (definition: FormDefinition, id: string): ContainerElement | undefined => {
  return allElements(definition.elements)
    .filter(isContainer)
    .find(container => childrenOf(container).some(element => element.id === id));
};

export const optionsOf = (field: FieldElement): Option[] => {
  const { settings } = field;
  return settings !== undefined && 'options' in settings ? (settings.options as Option[]) : [];
};

export const optionIdsOf = (field: FieldElement): Set<string> => {
  return new Set(optionsOf(field).map(option => option.id));
};

export const defaultOptionIds = (field: FieldElement): string[] =>
  [field.defaultValue].flat().filter((id): id is string => typeof id === 'string');

export type IdPrefix = 'fld' | 'grp' | 'sec' | 'txt' | 'opt' | 'fil';

const newId = (prefix: IdPrefix, inUse: Set<string>): string => {
  for (;;) {
    const id = `${prefix}_${crypto.randomUUID().replaceAll('-', '')}`;
    if (!inUse.has(id)) return id;
  }
};

const idsInUse = (definition: FormDefinition): Set<string> => {
  const ids = new Set<string>();
  for (const element of allElements(definition.elements)) {
    ids.add(element.id);
    if (element.kind === 'field') for (const option of optionsOf(element)) ids.add(option.id);
  }
  return ids;
};

export const createElementIdGenerator = (definition: FormDefinition): ((prefix: IdPrefix) => string) => {
  const inUse = idsInUse(definition);
  return prefix => {
    const id = newId(prefix, inUse);
    inUse.add(id);
    return id;
  };
};
